import { useEffect, useState, type ChangeEvent, type CSSProperties } from "react";

interface Props {
  count: number;
  onChange: (next: number) => void;
  onResetCocktail: () => void;
  onClearChecks: () => void;
}

export default function MultiplierControl({ count, onChange, onResetCocktail, onClearChecks }: Props) {
  // Local draft string so the input isn't blown away mid-edit by
  // `count` snapping back when parseInt("") returns NaN.
  const [draft, setDraft] = useState<string>(String(count));
  useEffect(() => { setDraft(String(count)); }, [count]);

  function setFromInput(e: ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    setDraft(raw);
    // Only commit when the draft is a positive integer; otherwise let
    // the user keep typing (committing happens on blur via commitDraft).
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 1 && n <= 99) onChange(n);
  }

  function commitDraft() {
    const n = parseInt(draft, 10);
    if (!Number.isFinite(n) || n < 1) {
      // Empty / invalid blur — snap back to the last committed count.
      setDraft(String(count));
    } else {
      const clamped = Math.min(99, n);
      if (clamped !== count) onChange(clamped);
      setDraft(String(clamped));
    }
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.label}>
        Batch · {count} cocktail{count === 1 ? "" : "s"}
      </div>
      <div style={styles.row}>
        <button
          type="button"
          onClick={() => onChange(count - 1)}
          style={styles.btn}
          aria-label="Decrease"
          disabled={count <= 1}
        >
          −
        </button>
        <input
          value={draft}
          onChange={setFromInput}
          onBlur={commitDraft}
          inputMode="numeric"
          type="number"
          min={1}
          max={99}
          style={styles.input}
          aria-label="Cocktail count"
          aria-valuemin={1}
          aria-valuemax={99}
          aria-valuenow={count}
        />
        <button
          type="button"
          onClick={() => onChange(count + 1)}
          style={styles.btn}
          aria-label="Increase"
          disabled={count >= 99}
        >
          +
        </button>
      </div>
      <div style={styles.inlineActions}>
        <button type="button" onClick={onResetCocktail} style={styles.linkBtn}>
          Reset cocktail
        </button>
        <span style={styles.dot}>·</span>
        <button type="button" onClick={onClearChecks} style={styles.linkBtn}>
          Clear checks
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    padding: 10,
    marginBottom: 10,
  },
  label: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    marginBottom: 8,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "44px minmax(0, 1fr) 44px",
    gap: 8,
    alignItems: "center",
  },
  btn: {
    height: 44,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    fontSize: 22,
    fontWeight: 900,
    cursor: "pointer",
  },
  input: {
    height: 44,
    borderRadius: 12,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    fontSize: 22,
    fontWeight: 900,
    textAlign: "center",
    boxSizing: "border-box",
    outline: "none",
  },
  inlineActions: {
    display: "flex",
    gap: 6,
    justifyContent: "flex-end",
    marginTop: 8,
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 800,
  },
  linkBtn: {
    background: "transparent",
    border: 0,
    color: "var(--muted)",
    fontWeight: 800,
    fontSize: 11,
    padding: 0,
    cursor: "pointer",
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  dot: { color: "var(--border)" },
};
